import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  Input,
  OnDestroy,
  QueryList,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import * as Rx from 'rxjs';

export enum ToggleButtonsColor {
  SECONDARY = 'secondary',
  TRANSPARENT_SECONDARY = 'transparent-secondary',
}

@Component({
  selector: 'fl-toggle-buttons-option',
  template: `
    <fl-button
      [buttonGroupFirst]="buttonGroupFirst"
      [buttonGroupLast]="buttonGroupLast"
      [buttonGroupMiddle]="buttonGroupMiddle"
      [color]="checkColor()"
      [size]="ButtonSize.SMALL"
      (click)="changeValue()"
    >
      <ng-content></ng-content>
    </fl-button>
  `,
})
export class ToggleButtonsOptionComponent {
  ButtonSize = ButtonSize;
  ButtonColor = ButtonColor;

  @Input() value: string;
  @Input() buttonGroupFirst: boolean;
  @Input() buttonGroupLast: boolean;
  @Input() buttonGroupMiddle: boolean;
  @Input() color = ToggleButtonsColor.SECONDARY;
  @Input() control: FormControl;

  checkColor() {
    return this.control && this.control.value === this.value
      ? ButtonColor.SECONDARY
      : this.getUnselectedButtonColor();
  }

  changeValue() {
    this.control.setValue(this.value);
  }

  private getUnselectedButtonColor() {
    switch (this.color) {
      case ToggleButtonsColor.TRANSPARENT_SECONDARY:
        return ButtonColor.TRANSPARENT_SECONDARY;

      default:
      case ToggleButtonsColor.SECONDARY:
        return ButtonColor.DEFAULT;
    }
  }
}

@Component({
  selector: 'fl-toggle-buttons',
  template: `
    <ng-content select="fl-toggle-buttons-option"></ng-content>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToggleButtonsComponent implements AfterContentInit, OnDestroy {
  @Input() color = ToggleButtonsColor.SECONDARY;
  @Input() control: FormControl;

  @ContentChildren(ToggleButtonsOptionComponent)
  options: QueryList<ToggleButtonsOptionComponent>;

  private optionsSubscription?: Rx.Subscription;

  ngAfterContentInit() {
    this.initOptions();
    this.optionsSubscription = this.options.changes.subscribe(() =>
      this.initOptions(),
    );
  }

  initOptions(): void {
    this.options.forEach((option, index) => {
      option.buttonGroupFirst = this.options.length > 1 && index === 0;
      option.buttonGroupLast =
        this.options.length > 1 && index === this.options.length - 1;
      option.buttonGroupMiddle =
        this.options.length > 2 &&
        !option.buttonGroupFirst &&
        !option.buttonGroupLast;
      option.control = this.control;
      option.color = this.color;
    });
  }

  ngOnDestroy() {
    if (this.optionsSubscription) {
      this.optionsSubscription.unsubscribe();
    }
  }
}
